import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Download, FileSpreadsheet, Layers3, LogOut, MessageCircle, ScanLine } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { useClientAuth } from "@/lib/clientAuth";
import { istDateString } from "@/lib/date";

const iso = date => date.toISOString().slice(0, 10);
const currentWeekBounds = () => {
  const today = new Date(`${istDateString()}T12:00:00+05:30`);
  const day = today.getUTCDay() || 7;
  const start = new Date(today);
  start.setUTCDate(today.getUTCDate() - day + 1);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 5);
  return { start: iso(start), end: iso(end) };
};
const CURRENT_WEEK = currentWeekBounds();
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };
const formatTime = value => value ? new Intl.DateTimeFormat("en-IN", { dateStyle:"medium", timeStyle:"short", timeZone:"Asia/Kolkata" }).format(new Date(value)) : "—";
const formatRangeDate = (value, withYear = false) => value ? new Intl.DateTimeFormat("en-IN", { day:"numeric", month:"short", ...(withYear ? { year:"numeric" } : {}) }).format(new Date(`${value}T12:00:00`)) : "—";
const dayLabel = value => value ? new Intl.DateTimeFormat("en-IN", { weekday:"short" }).format(new Date(`${value}T12:00:00`)) : "—";

export default function ClientPortal() {
  const { client, logout } = useClientAuth();
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(CURRENT_WEEK.start);
  const [toDate, setToDate] = useState(CURRENT_WEEK.end);
  const [view, setView] = useState("daywise");
  const [data, setData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [weeklyLoading, setWeeklyLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const res = await api.get("/client-portal/summary", { params:{ from_date:fromDate, to_date:toDate } }); setData(res.data); }
    catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let active = true;
    setWeeklyLoading(true);
    api.get("/client-portal/summary", { params:{ from_date:CURRENT_WEEK.start, to_date:CURRENT_WEEK.end } })
      .then(res => { if (active) setWeeklyData(res.data); })
      .catch(() => { if (active) setWeeklyData(null); })
      .finally(() => { if (active) setWeeklyLoading(false); });
    return () => { active = false; };
  }, []);
  const exports = ext => `${API}/client-portal/export.${ext}?view=${view}&from_date=${fromDate}&to_date=${toDate}`;
  const sizeRows = useMemo(() => [...(data?.size_totals||[]), ...(data?.baking_size_totals||[])], [data]);
  const dayGroups = useMemo(() => data?.day_groups || [], [data]);
  const weeklyDays = useMemo(() => {
    const byDate = new Map((weeklyData?.day_groups || []).map(group => [group.date, group]));
    return Array.from({ length:6 }, (_, index) => {
      const date = new Date(`${CURRENT_WEEK.start}T12:00:00+05:30`);
      date.setUTCDate(date.getUTCDate() + index);
      const dateKey = iso(date);
      return { date:dateKey, plates:byDate.get(dateKey)?.total_plates || 0 };
    });
  }, [weeklyData]);
  const maxWeeklyPlates = useMemo(() => Math.max(1, ...weeklyDays.map(day => day.plates)), [weeklyDays]);

  return <main className="client-portal min-h-screen">
    <header className="client-portal-header sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4">
        <img className="w-36 sm:w-44" src="/images/jyoti-image-logo.png" alt="Jyoti Image" />
        <div className="flex items-center gap-3">
          <div className="hidden sm:block text-right"><strong className="block text-sm">{client.client_name}</strong><span className="text-xs text-zinc-500">Client account · Secure portal</span></div>
          <div className="client-avatar" aria-hidden="true">{client.client_name?.split(/\s+/).slice(0,2).map(word=>word[0]).join("").toUpperCase()}</div>
          <button onClick={async()=>{await logout();navigate("/client-login");}} className="client-outline-button"><LogOut size={15} /><span className="hidden sm:inline">Sign out</span></button>
        </div>
      </div>
      <div className="client-cmyk-strip"><i/><i/><i/><i/></div>
    </header>

    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-5">
      <section className="client-welcome-card">
        <div className="client-welcome-copy">
          <div className="client-kicker">Your print activity, beautifully organised</div>
          <h1>{greeting()}!<br/><span>Welcome, {client.client_name}.</span></h1>
          <p>Thank you for your continued trust in Jyoti Image. Every assignment matters to us, and your latest job and plate activity is presented below.</p>
          <div className="client-promise"><strong>From plate to print</strong><i/>Precision · Reliability · Commitment</div>
        </div>
        <div className="client-welcome-art" aria-hidden="true">
          <div className="client-paper-stack">
            <div className="client-paper-back" />
            <div className="client-paper-card">
              <small>From plate to print</small>
              <strong>Every assignment matters.</strong>
              <div className="client-paper-colours"><i/><i/><i/><i/></div>
              <span>Prepared with precision and care.</span>
            </div>
          </div>
          <div className="client-period"><small>Showing activity for</small><strong>{formatRangeDate(fromDate)}–{formatRangeDate(toDate, true)}</strong></div>
        </div>
      </section>

      <section className="client-filter-card">
        <label>From date<input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} /></label>
        <label>To date<input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} /></label>
        <button onClick={load} className="client-apply-button"><CalendarCheck size={17}/>Apply dates</button>
        <div className="client-export-actions">
          <a href={exports("pdf")}><Download size={16}/>Export PDF</a>
          <a href={exports("xlsx")}><FileSpreadsheet size={16}/>Export Excel</a>
        </div>
      </section>

      {error && <div className="client-error">{error}</div>}

      <section className="client-stat-grid" aria-label="Selected period summary">
        <article className="client-stat-card client-stat-cyan">
          <div className="client-stat-icon"><Layers3 size={18}/></div>
          <span>Total jobs</span><strong>{loading ? "—" : data?.total_jobs||0}</strong><small>In the selected period</small>
        </article>
        <article className="client-stat-card client-stat-magenta">
          <div className="client-stat-icon"><ScanLine size={18}/></div>
          <span>Total plates</span><strong>{loading ? "—" : data?.total_plates||0}</strong><small>In the selected period</small>
        </article>
      </section>

      <section>
        <div className="client-section-heading"><div><div className="client-kicker">Current week · Monday to Saturday</div><h2>Activity at a glance</h2></div><p>This overview always shows your plate output for the current working week, irrespective of the custom report dates selected above.</p></div>
        <div className="client-activity-card">
          <div className="client-card-heading"><h3>Daily plate output</h3><span>{weeklyLoading ? "Loading…" : `${weeklyData?.total_plates||0} plates this week`}</span></div>
          {weeklyLoading ? <div className="client-chart-empty">Loading weekly plate activity…</div> :
            <div className="client-bar-chart" role="img" aria-label="Daily plate output for the current Monday to Saturday working week">
              {weeklyDays.map((day,index)=>{
                return <div className="client-bar-item" key={day.date}>
                  <b>{day.plates}</b>
                  <div className={`client-bar client-bar-${index%4}`} style={{height:`${Math.max(4,(day.plates/maxWeeklyPlates)*100)}%`}} />
                  <small>{dayLabel(day.date)}</small>
                </div>;
              })}
            </div>}
        </div>
      </section>

      <section className="client-report-card">
        <div className="client-report-header">
          <h2>Detailed job report</h2>
          <div className="client-tabs" role="tablist" aria-label="Report view">
            <button role="tab" aria-selected={view==="daywise"} onClick={()=>setView("daywise")}>Day-wise activity</button>
            <button role="tab" aria-selected={view==="billwise"} onClick={()=>setView("billwise")}>Bill-wise summary</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {loading ? <div className="client-report-empty">Loading activity…</div> : view === "daywise" ?
            <div>{dayGroups.map(group=><div key={group.date} className="client-day-group"><div className="client-day-heading"><strong>{group.date}</strong><span>{group.jobs?.length||0} jobs · {group.total_plates} plates</span></div><table className="client-report-table min-w-[900px]"><thead><tr><th>Time entered</th><th>Job name</th><th>File name</th><th>Plate size</th><th>Sets</th><th>Colour</th><th className="text-right">Plates</th></tr></thead><tbody>{group.jobs.map(job=><tr key={job.id}><td className="whitespace-nowrap">{formatTime(job.entered_at)}</td><td className="font-semibold">{job.job_name}</td><td>{job.file_name||"—"}</td><td><span className="client-size-pill">{job.plate_size}</span></td><td>{job.sets}</td><td><span className="client-colour-pill">{job.colours}</span></td><td className="text-right font-bold">{job.final_plate_qty}</td></tr>)}</tbody></table></div>)}{!dayGroups.length&&<div className="client-report-empty">No jobs in this period.</div>}</div>
            : <table className="client-report-table min-w-[850px]"><thead><tr><th>Plate size</th><th className="text-right">1 colour</th><th className="text-right">2 colour</th><th className="text-right">3 colour</th><th className="text-right">4 colour</th><th className="text-right">Sets</th><th className="text-right">Additional</th><th className="text-right">Plates</th></tr></thead><tbody>{sizeRows.map((row,i)=><tr key={`${row.plate_size_id}-${i}`}><td className="font-bold"><span className="client-size-pill">{row.canonical}</span></td><td className="text-right">{row.one_colour_sets}</td><td className="text-right">{row.two_colour_sets}</td><td className="text-right">{row.three_colour_sets}</td><td className="text-right">{row.four_colour_sets}</td><td className="text-right">{row.total_sets}</td><td className="text-right">{row.additional_plates}</td><td className="text-right font-bold">{row.qty}</td></tr>)}</tbody></table>}
        </div>
      </section>
    </div>

    <a className="client-whatsapp" href="https://wa.me/918585849393?text=Hello%20Jyoti%20Image" target="_blank" rel="noreferrer"><MessageCircle size={20}/>WhatsApp us</a>
  </main>;
}
